import React, { useState, useMemo } from 'react';
import { Select } from 'antd';
import { observer } from 'mobx-react';
import { LabeledValue, SelectProps } from 'antd/es/select';
import Suggestion from '../Suggestion';
import { CoatingsProps } from '@partsbadger/types';
import QuoteStore from '../../stores/QuoteStore';

interface Props extends SelectProps {
    coatings: Array<CoatingsProps>;
    suggestions?: {
        id: number;
        name: string;
        thumbnail: string;
    }[];
    value?: LabeledValue | null;
    for_staff?: boolean;
}

export const Coatings = observer((props: Props) => {
    const [isDropDownMenuOpen, setIsDropDownMenuOpen] = useState(false);

    let items = props.coatings;

    if (QuoteStore?.quote?.contact?.is_hobbyist) {
        items = items?.filter(item => {
            return item.name !== 'Review 2D';
        });
    }

    // Get Custom Option and build the menu efficiently
    const { custom, filteredItems } = useMemo<{
        custom?: CoatingsProps;
        filteredItems: CoatingsProps[];
    }>(() => {
        // Find the "Custom" option
        const customItem = items.find(item => item.name === 'Custom');

        // Exclude "Custom" from the list to avoid duplication in the Options group
        const filtered = items.filter(item => item.id !== customItem?.id);

        // Stable comparator: put "None" first, keep others in their original order
        const sorted = filtered.slice().sort((a, b) => {
            const aIsNone = a.name === 'None';
            const bIsNone = b.name === 'None';
            if (aIsNone && !bIsNone) return -1;
            if (!aIsNone && bIsNone) return 1;
            return 0;
        });

        return { custom: customItem, filteredItems: sorted };
    }, [items]);

    const menu = useMemo(
        () =>
            filteredItems?.map(item => (
                <Select.Option key={item.id} value={item.id}>
                    {item.name}
                </Select.Option>
            )),
        [filteredItems]
    );

    const EmptyTag = (
        <div
            className="flex flex-no-wrap p-2 cursor-pointer"
            onClick={() => {
                if (custom && props.onChange) {
                    props.onChange(
                        {
                            key: custom.id.toString(),
                            label: custom.name,
                        },
                        []
                    );
                    setIsDropDownMenuOpen(false);
                }
            }}
        >
            <span className="flex flex-row items-center cursor-pointer justify-center">
                Can't find your Coating? please select <div className="ml-2 link">Custom</div>
            </span>
        </div>
    );

    return (
        <>
            <Select
                showSearch
                labelInValue
                bordered={true}
                size={props?.for_staff ? 'middle' : 'small'}
                dropdownMatchSelectWidth
                placeholder="Select Coating"
                open={isDropDownMenuOpen}
                onDropdownVisibleChange={visible => setIsDropDownMenuOpen(visible)}
                data-testid="coating-select"
                {...props}
                notFoundContent={EmptyTag}
                allowClear={false}
                filterOption={(input, option: any) => {
                    try {
                        if (!option.key) {
                            // Filter by group label
                            return option.props?.label?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
                        }

                        return option.props?.children?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0;
                    } catch (e) {
                        return false;
                    }
                }}
                dropdownRender={menu => <div>{menu}</div>}
            >
                {!QuoteStore?.quote?.contact?.is_hobbyist && custom && custom?.id > 0 && (
                    <Select.OptGroup label={'Custom'}>
                        <Select.Option key={custom.id}>{custom.name}</Select.Option>
                    </Select.OptGroup>
                )}
                <Select.OptGroup label={'Options'}>{menu}</Select.OptGroup>
            </Select>

            {!props.value?.label && (
                <div className="overflow-auto" style={{ maxHeight: '75px' }}>
                    {props.suggestions?.map(suggestion => (
                        <Suggestion
                            key={suggestion.id}
                            title={suggestion.name}
                            thumbnail={suggestion.thumbnail}
                            handleClick={() => {
                                const val = {
                                    key: suggestion.id.toString(),
                                    label: suggestion.name,
                                    value: suggestion.id.toString(),
                                };

                                if (props.onSelect) {
                                    props.onSelect(val, val);
                                }

                                if (props.onChange) {
                                    props.onChange(val, []);
                                }
                            }}
                        />
                    ))}
                </div>
            )}
        </>
    );
});
